import React from 'react';
import { Package, Users, AlertCircle } from 'lucide-react';
import type { Product } from '@/types/product';

interface ProjectsTabProps {
  products: Product[];
}

export function ProjectsTab({ products }: ProjectsTabProps) {
  // Filter products that are in development phases
  const developmentProducts = products.filter(p => 
    ['design', 'engineering', 'testing', 'manufacturing'].includes(p.status)
  );

  const roleDisplayNames: Record<string, string> = {
    designer: 'Designers',
    engineer: 'Engineers',
    tester: 'Testers',
    assemblyWorker: 'Assembly Workers'
  };

  const phaseDisplayNames: Record<string, string> = {
    design: 'Design',
    engineering: 'Engineering',
    testing: 'Testing',
    manufacturing: 'Manufacturing'
  };

  const phaseColors: Record<string, string> = {
    design: 'bg-theme-warning/20 text-theme-warning',
    engineering: 'bg-theme-accent/20 text-theme-accent',
    testing: 'bg-theme-info/20 text-theme-info',
    manufacturing: 'bg-theme-warning/20 text-theme-warning'
  };

  if (developmentProducts.length === 0) {
    return (
      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-theme-text-muted mb-4" />
          <h3 className="text-lg font-medium text-theme-text-primary mb-2">No Active Projects</h3>
          <p className="text-theme-text-muted">
            No products are currently in development. Start a new product from the Products tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="px-6 py-4 border-b border-theme-border">
          <h3 className="text-lg font-medium text-theme-text-primary">Active Development Projects</h3>
          <p className="mt-1 text-sm text-theme-text-muted">
            Employee allocations for products in development
          </p>
        </div>

        <div className="px-6 py-4">
          <div className="space-y-6">
            {developmentProducts.map(product => {
              const allocatedEmployees = product.developmentPhase?.allocatedEmployees || {};
              const hasAllocations = Object.keys(allocatedEmployees).length > 0;
              const plannedAllocations = product.plannedAllocations;

              return (
                <div key={product.id} className="border rounded-lg p-4 bg-theme-bg-secondary">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-medium text-theme-text-primary">{product.name}</h4>
                      <div className="flex items-center mt-1 space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${phaseColors[product.status]}`}>
                          {phaseDisplayNames[product.status] || product.status}
                        </span>
                        <span className="text-sm text-theme-text-muted">
                          Tech Score: {product.techScore.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Current Phase Allocation */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-theme-text-secondary mb-2 flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      Current Phase ({phaseDisplayNames[product.status]})
                    </h5>
                    {hasAllocations ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(allocatedEmployees).map(([role, count]) => (
                          <div key={role} className="bg-theme-bg-content rounded p-2 border border-theme-border">
                            <div className="text-xs text-theme-text-muted">{roleDisplayNames[role] || role}</div>
                            <div className="text-lg font-semibold text-theme-text-primary">{count}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center text-theme-warning text-sm">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        No employees allocated - development paused
                      </div>
                    )}
                  </div>

                  {/* Planned Allocations for Future Phases */}
                  {plannedAllocations && (
                    <div>
                      <h5 className="text-sm font-medium text-theme-text-secondary mb-2">Planned Allocations</h5>
                      <div className="space-y-2">
                        {(['design', 'engineering', 'testing', 'manufacturing', 'production'] as const)
                          .filter(phase => phase !== product.status && plannedAllocations[phase])
                          .map(phase => {
                            const allocation = plannedAllocations[phase];
                            const hasEmployees = Object.values(allocation).some(count => count > 0);
                            
                            if (!hasEmployees) return null;

                            return (
                              <div key={phase} className="flex items-center space-x-3 text-sm">
                                <span className="font-medium text-theme-text-secondary w-24">
                                  {phaseDisplayNames[phase] || phase}:
                                </span>
                                <div className="flex items-center space-x-4">
                                  {Object.entries(allocation).map(([role, count]) => (
                                    count > 0 && (
                                      <span key={role} className="text-theme-text-secondary">
                                        {count} {roleDisplayNames[role] || role}
                                      </span>
                                    )
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-theme-text-primary mb-4">Employee Allocation Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['designer', 'engineer', 'tester', 'assemblyWorker'].map(role => {
            const totalAllocated = developmentProducts.reduce((sum, product) => {
              const allocated = product.developmentPhase?.allocatedEmployees?.[role] || 0;
              return sum + allocated;
            }, 0);

            return (
              <div key={role} className="bg-theme-bg-secondary rounded-lg p-4">
                <div className="text-sm text-theme-text-muted">{roleDisplayNames[role]}</div>
                <div className="text-2xl font-semibold text-theme-text-primary mt-1">
                  {totalAllocated}
                </div>
                <div className="text-xs text-theme-text-muted mt-1">Allocated</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}